import { useEffect, useRef, useState } from 'react';
import { request } from '../shared/api/request';
import { useStore } from '../context/StoreContext';
import { Icon } from '../shared/ui/Icon';

export function AuthModal() {
  const {
    authMode, authError, handleAuth, setAuthOpen, setAuthMode, setAuthError,
  } = useStore();
  const [showPassword, setShowPassword] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [formMessage, setFormMessage] = useState('');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dialogRef = useRef(null);
  const emailRef = useRef(null);

  useEffect(() => {
    const previousFocus = document.activeElement;
    emailRef.current?.focus();
    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setAuthOpen(false);
      } else if (event.key === 'Tab' && dialogRef.current) {
        const focusable = [...dialogRef.current.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href]')];
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previousFocus?.focus?.();
    };
  }, [setAuthOpen]);

  async function handleForgotPassword(event) {
    event.preventDefault();
    setIsSubmitting(true);
    setFormMessage('');
    setFormError('');
    try {
      await request('/api/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email: new FormData(event.currentTarget).get('email') }),
      });
      setFormMessage('If an account exists for this email, password reset instructions will be sent.');
    } catch (error) {
      setFormError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setAuthOpen(false); }}>
      <section ref={dialogRef} className="auth-modal" role="dialog" aria-modal="true" aria-labelledby="auth-title">
        <button className="modal-close" aria-label="Close sign-in dialog" onClick={() => setAuthOpen(false)}>×</button>
        <p className="eyebrow">WELCOME TO TECHNOMAX</p>
        <h2 id="auth-title">
          {forgotMode ? 'Reset your password.' : authMode === 'login' ? 'Good to see you.' : 'A good place to start.'}
        </h2>
        <p>
          {forgotMode
            ? 'Enter your email and we’ll send password reset instructions if your account exists.'
            : authMode === 'login'
              ? 'Sign in to continue with your order.'
              : 'Create an account and keep everything in one place.'}
        </p>
        <form onSubmit={forgotMode ? handleForgotPassword : handleAuth}>
          {authMode === 'register' && !forgotMode && (
            <label>Your name<input name="name" required autoComplete="name" placeholder="Full name" /></label>
          )}
          <label>Email address<input ref={emailRef} name="email" type="email" required autoComplete="email" placeholder="you@example.com" /></label>
          {!forgotMode && (
            <label>Password
              <span className="password-field">
                <input name="password" type={showPassword ? 'text' : 'password'} required minLength="6" autoComplete={authMode === 'login' ? 'current-password' : 'new-password'} placeholder="At least 6 characters" />
                <button type="button" aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} onClick={() => setShowPassword((shown) => !shown)}>
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </span>
            </label>
          )}
          {!forgotMode && authMode === 'login' && (
            <button className="forgot-password-link" type="button" onClick={() => { setForgotMode(true); setFormMessage(''); setFormError(''); setAuthError(''); }}>
              Forgot password?
            </button>
          )}
          <button className="button button-primary full-width" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Please wait…' : forgotMode ? 'Send reset link' : authMode === 'login' ? 'Sign in' : 'Create account'} <Icon>→</Icon>
          </button>
        </form>
        {(authError || formError || formMessage) && <p className={authError || formError ? 'auth-error' : 'auth-form-message'} role={authError || formError ? 'alert' : 'status'}>{authError || formError || formMessage}</p>}
        <div className="auth-switch">
          {forgotMode ? (
            <button onClick={() => { setForgotMode(false); setFormMessage(''); setFormError(''); }}>Back to sign in</button>
          ) : (
            <>
              {authMode === 'login' ? 'New to Technomax?' : 'Already have an account?'}{' '}
              <button onClick={() => { setAuthMode(authMode === 'login' ? 'register' : 'login'); setAuthError(''); }}>
                {authMode === 'login' ? 'Create account' : 'Sign in'}
              </button>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
